/**
 * PostHog analytics client and event catalog
 *
 * @overview
 * Single source of truth for product analytics. All customer-facing events flow
 * through this module so event names stay consistent and funnel definitions in
 * PostHog do not break when call sites are refactored.
 *
 * @usage
 * ```typescript
 * import { capture, EVENTS } from '@/lib/posthog';
 *
 * capture(EVENTS.WHEEL_SPUN, { campaignKey, prizeName });
 * ```
 *
 * @security
 * Never pass PII as event properties: emails, full names, phone numbers, or the
 * Amazon order ID itself. Order IDs identify a purchase and are treated as
 * customer data — send only whether verification passed and why it failed.
 * See the logging rules in `@/lib/logger`.
 */

import posthog from 'posthog-js';
import { isDevelopment } from './environment';
import { getStoredAttribution } from './attributionCapture';
import { trackMetaEvent } from './metaPixel';
import { META_EVENTS } from '../../convex/lib/attribution';

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

/**
 * Scratch & Win acquisition funnel, in order.
 *
 * The live flow is the P0 simplified 3-step (`wheel → claim → links`). Users
 * spin anonymously before the sign-in gate, so `identify()` on sign-in is what
 * keeps steps 1-2 attached to steps 4-9 as one person.
 */
export const EVENTS = {
  /** 1. Landed on the promo page, typically from an insert-card QR scan. */
  PROMO_LANDED: 'promo_landed',
  /** 2. Wheel spin resolved to a prize (still anonymous at this point). */
  WHEEL_SPUN: 'wheel_spun',
  /** 3. Reached the sign-in gate that sits between the prize and its delivery. */
  SIGNIN_GATE_VIEWED: 'signin_gate_viewed',
  /** 4. Clerk authentication completed; anonymous session now stitched. */
  SIGNIN_COMPLETED: 'signin_completed',
  /** 5. Submitted an Amazon order ID for verification. */
  ORDER_ID_SUBMITTED: 'order_id_submitted',
  /** 6. Verification rejected the submission. Carries `reason_category`. */
  ORDER_ID_VERIFICATION_FAILED: 'order_id_verification_failed',
  /** 7. Claim persisted. */
  CLAIM_SUBMITTED: 'claim_submitted',
  /** 8. Promo links rendered — the prize is now deliverable. */
  PROMO_LINKS_VIEWED: 'promo_links_viewed',
  /** 9. Clicked through to Amazon. The conversion event. */
  PROMO_LINK_CLICKED: 'promo_link_clicked',
  /**
   * Tapped the individual claim code to copy it (pool-drawn codes only). Not a
   * funnel step: it sits between 8 and 9 as a signal that the three-step
   * instructions are being followed, and a copy without a later
   * PROMO_LINK_CLICKED is the shape of a customer who took the code to Amazon
   * on their own.
   */
  PROMO_CODE_COPIED: 'promo_code_copied',

  /**
   * Collector quiz. Deliberately numbered outside the promo funnel above: the
   * quiz is offered AFTER the prize is delivered, so it must never be mistaken
   * for a step between the scan and the reward.
   */
  /** Quiz invitation rendered on a funnel surface. Carries `source`. */
  QUIZ_OFFERED: 'quiz_offered',
  /** First question answered — the taker actually engaged. Carries `source`. */
  QUIZ_STARTED: 'quiz_started',
  /** All questions answered and a recommendation shown. Carries `source`. */
  QUIZ_COMPLETED: 'quiz_completed',

  /**
   * D2C checkout. Separate from the promo and quiz funnels above because this
   * is the only path that produces revenue on our own site rather than sending
   * the customer to Amazon.
   */
  /** Reached /checkout with a non-empty cart. */
  CHECKOUT_STARTED: 'checkout_started',
  /** Pending order created and the customer is being sent to Stripe. */
  CHECKOUT_REDIRECTED_TO_PAYMENT: 'checkout_redirected_to_payment',

  /**
   * Outbound click to Amazon from anywhere that is NOT the prize-delivery
   * screen. Until on-site checkout is enabled this is the only conversion a
   * visitor can complete, so it is the terminal step of the ordinary browse
   * funnel.
   *
   * Deliberately NOT `PROMO_LINK_CLICKED`. That event is step 9 of the scratch
   * & win funnel and its target is stated as a percentage of *verified
   * winners* (`docs/marketing/KPI_TREE.md` §2), so firing it from a product
   * page would inflate that funnel's conversion with clicks that never had a
   * prize. Two different conversions that happen to share a destination still
   * need two names.
   *
   * Carries `source` (which surface, an `AMAZON_REF_SOURCES` value), `asin`
   * (null for store-level links), `path` (which page), and `channel`
   * (first-touch attribution, so a paid visitor is distinguishable from an
   * organic one). Prefer `captureAmazonCtaClick()` over calling this directly
   * — it fills `path` and `channel` so call sites cannot drift apart.
   */
  AMAZON_CTA_CLICKED: 'amazon_cta_clicked',

  /**
   * Footer newsletter submitted. Named `_started`, not `_subscribed`, because
   * that is all it is: there is no anonymous subscriber store, so the address
   * only becomes a subscription once the visitor finishes signup and ticks
   * consent on `/welcome`. Pair it with the consent mutation to get the real
   * completion rate — treating this as a subscription would overcount.
   */
  NEWSLETTER_SIGNUP_STARTED: 'newsletter_signup_started',

  /** Any hard stop in the funnel, categorised by who can fix it. */
  FUNNEL_BLOCKED: 'funnel_blocked',
  /** World selection on the sports/fantasy landing pages. */
  WORLD_SELECTED: 'world_selected',

  /**
   * THE COMBINATION — the collector game at /vault.
   *
   * Deliberately named so none of these can be mistaken for a scratch & win
   * step. That funnel's targets are stated as percentages of QR scans and
   * verified winners (docs/marketing/KPI_TREE.md §2); folding a second game's
   * volume into it would inflate a number nobody could then un-inflate.
   */
  /** The game was offered on a surface. Carries `source`. */
  COMBINATION_OFFERED: 'combination_offered',
  /** First turn answered — they actually engaged. Carries `source`. */
  COMBINATION_STARTED: 'combination_started',
  /** All turns answered and the vault opened. Carries `archetype`, `turn_count`. */
  COMBINATION_COMPLETED: 'combination_completed',

  /**
   * The monthly collector code. `incentive_code_issued` fires when a code is
   * shown to its owner for the first time, NOT when the cron writes it — the
   * client cannot see the cron. Server-side issuance is counted in Convex.
   */
  INCENTIVE_CODE_ISSUED: 'incentive_code_issued',
  INCENTIVE_CODE_VIEWED: 'incentive_code_viewed',
  INCENTIVE_CODE_COPIED: 'incentive_code_copied',

  /** The concierge panel. */
  CONCIERGE_OPENED: 'concierge_opened',
  CONCIERGE_ANSWERED: 'concierge_answered',
  CONCIERGE_TICKET_CREATED: 'concierge_ticket_created',
  /** The agent reached out first, at a measured pause. `reason` says which one. */
  CONCIERGE_NUDGED: 'concierge_nudged',

  // The collector conversation (homepage invitation, concierge interview mode)
  CONVERSATION_OFFERED: 'conversation_offered',
  CONVERSATION_STARTED: 'conversation_started',
  CONVERSATION_CONSENT_GIVEN: 'conversation_consent_given',
  CONVERSATION_COMPLETED: 'conversation_completed',
  CONVERSATION_REWARD_OPENED: 'conversation_reward_opened',

  // The insert-card landing (/promo/insert/:campaignKey); landing itself is PROMO_LANDED
  INSERT_CLAIM_SUBMITTED: 'insert_claim_submitted',
  INSERT_REWARD_OPENED: 'insert_reward_opened',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Why a customer could not proceed. Drives whether a drop-off is a UX problem,
 * an expected dead end, or an outage on our side.
 */
export const BLOCK_REASONS = {
  /** Bad input we can fix with copy — e.g. a shipment ID pasted as an order ID. */
  USER_ERROR: 'user_error',
  /** One-prize-per-customer dead end. Expected by design. */
  ALREADY_CLAIMED: 'already_claimed',
  /** Clerk and Convex disagree about who the user is. Our bug. */
  AUTH_DESYNC: 'auth_desync',
  /** Prizes exhausted or misconfigured. Our bug, and a total funnel outage. */
  CONFIG_ERROR: 'config_error',
  /** Anything we have not classified yet. */
  UNKNOWN: 'unknown',
} as const;

export type BlockReason = (typeof BLOCK_REASONS)[keyof typeof BLOCK_REASONS];

/**
 * Map a backend error message to the party who can fix it.
 *
 * The Convex functions in `convex/scratchWin.ts` throw distinct messages;
 * counting drop-offs only tells us people left, whereas categorising the cause
 * separates "our promotion is broken" from "they pasted the wrong ID".
 *
 * Order matters: config and auth failures are checked before the user-error
 * fallback so an infrastructure problem is never misfiled as customer error.
 */
export function classifyBlockReason(message: string): BlockReason {
  const m = message.toLowerCase();

  // Our bug, and a total funnel outage — every customer is walled at step 1.
  if (
    m.includes('prize_config_error') ||
    m.includes('no active prizes') ||
    m.includes('prize not found') ||
    m.includes('probabilities sum')
  ) {
    return BLOCK_REASONS.CONFIG_ERROR;
  }

  // Clerk and Convex disagree about identity.
  if (
    m.includes('not authenticated') ||
    m.includes('user not found') ||
    m.includes('user id mismatch') ||
    m.includes('game attempt not found')
  ) {
    return BLOCK_REASONS.AUTH_DESYNC;
  }

  // Expected dead end: one prize per customer.
  if (
    m.includes('already claimed') ||
    m.includes('already submitted a claim') ||
    m.includes('already played') ||
    m.includes('already been used')
  ) {
    return BLOCK_REASONS.ALREADY_CLAIMED;
  }

  // Fixable with better copy or placeholder text.
  if (
    m.includes('order id') ||
    m.includes('invalid session') ||
    m.includes('required') ||
    m.includes('characters long') ||
    m.includes('invalid consent')
  ) {
    return BLOCK_REASONS.USER_ERROR;
  }

  return BLOCK_REASONS.UNKNOWN;
}

/** Which deployed environment this bundle is running in. */
export type AnalyticsEnvironment = 'local' | 'staging' | 'production' | 'unknown';

/**
 * Identify the environment from the hostname.
 *
 * Staging and production currently report into the SAME PostHog project, so
 * every event carries this as a super property. Without it, a staging test walk
 * is indistinguishable from real customer behaviour and would quietly corrupt
 * the funnel numbers we make decisions on.
 *
 * Derived from the hostname rather than a build-time flag so a promoted bundle
 * cannot carry the wrong label.
 */
export function detectEnvironment(hostname: string): AnalyticsEnvironment {
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')) {
    return 'local';
  }
  // Check staging first: it is also a *.infinityvaultcards.com host.
  if (hostname.startsWith('staging.') || hostname.includes('amplifyapp.com')) {
    return 'staging';
  }
  if (hostname.endsWith('infinityvaultcards.com')) {
    return 'production';
  }
  return 'unknown';
}

/** True when PostHog is configured. Absent keys make every helper a no-op. */
export function isAnalyticsEnabled(): boolean {
  return Boolean(POSTHOG_KEY && POSTHOG_HOST);
}

/**
 * Initialise the browser client. Safe to call more than once.
 *
 * `capture_pageview` is disabled because this is a wouter SPA — the built-in
 * handler only fires on the initial document load, so `useAnalytics()` drives
 * pageviews on route change instead.
 */
export function initPostHog(): void {
  if (!isAnalyticsEnabled() || posthog.__loaded) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Anonymous wheel spins should not create person profiles; identified users do.
    person_profiles: 'identified_only',
    // Driven manually from useAnalytics() — wouter navigation is client-side.
    capture_pageview: false,
    capture_pageleave: true,
    // Exceptions flow into PostHog error tracking. Same consent gate as every
    // other capture — this only runs after initPostHog(), which consent gates.
    // Stack traces from our minified bundle carry no PII; inputs stay masked
    // by the session_recording config below.
    capture_exceptions: true,
    session_recording: {
      // The Amazon order ID is typed into this flow. Never record input values.
      maskAllInputs: true,
    },
    // Surface init problems locally; stay quiet in production.
    loaded: (ph) => {
      if (isDevelopment()) ph.debug();
    },
  });

  // Super property: attached to EVERY event, including ones fired before
  // identify(). Staging and production share a PostHog project, so this is what
  // keeps a staging test walk out of the real funnel. Filter on it in every
  // insight — an untagged funnel mixes the two.
  posthog.register({ environment: detectEnvironment(window.location.hostname) });
}

/**
 * Record a product event. No-ops when PostHog is unconfigured, which keeps
 * local development and the test suite silent rather than throwing.
 */
export function capture(
  event: EventName,
  properties?: Record<string, unknown>
): void {
  // `isAnalyticsEnabled()` only says the keys are configured. Consent gating
  // means `initPostHog()` may never have run, and posthog-js answers a call on
  // an uninitialised client with a `console.error` per call. On the Amazon CTA
  // that is one error per click for every visitor who declined cookies, which
  // would drown real errors. Same `__loaded` check `initPostHog` uses.
  if (!isAnalyticsEnabled() || !posthog.__loaded) return;
  posthog.capture(event, properties);
}

/** Record a pageview for the current location. */
export function capturePageView(path: string): void {
  if (!isAnalyticsEnabled()) return;
  posthog.capture('$pageview', { $current_url: window.location.href, path });
}

/**
 * Attach the anonymous session to a known user.
 *
 * Identifies by Clerk user ID only — no email or name, per the PII rules in
 * `@/lib/logger`.
 */
export function identifyUser(userId: string): void {
  if (!isAnalyticsEnabled()) return;
  posthog.identify(userId);
}

/** Clear identity on sign-out so the next visitor is not attributed to them. */
export function resetIdentity(): void {
  if (!isAnalyticsEnabled()) return;
  posthog.reset();
}

/**
 * Record an outbound click to Amazon.
 *
 * Until on-site checkout is enabled this is the only conversion a visitor can
 * complete, so every Amazon CTA on the site funnels through here to keep one
 * property vocabulary — a second shape would make the surfaces uncomparable.
 *
 * Attribution is read at click time rather than passed in, so callers cannot
 * disagree about what "channel" means. Only the channel enum and campaign id
 * are sent; the click id is deliberately withheld as a per-visitor identifier.
 *
 * @param source Which surface the click came from — use `AMAZON_REF_SOURCES`.
 * @param asin   The product clicked through to, or null for store-level links.
 */
export function captureAmazonCtaClick(source: string, asin: string | null = null): void {
  // Meta sees the same click as a standard Lead: purchase intent declared
  // on-site, conversion completing off-site on Amazon. Lead (not a custom
  // event) so ad optimisation and attribution need no Events Manager config.
  // No eventId — there is no server-side counterpart to dedup against.
  // trackMetaEvent gates itself on consent + dataset, independent of PostHog's
  // gate below: one platform being unconfigured must not silence the other.
  try {
    trackMetaEvent(META_EVENTS.LEAD, {
      content_name: 'amazon_cta',
      content_category: source,
      ...(asin ? { content_type: 'product', content_ids: [asin] } : {}),
    });
  } catch {
    // Never let analytics break the customer's navigation to Amazon.
  }

  if (!isAnalyticsEnabled()) return;

  // Never let an analytics read break the customer's navigation to Amazon.
  let attribution: ReturnType<typeof getStoredAttribution> = null;
  try {
    attribution = getStoredAttribution();
  } catch {
    attribution = null;
  }

  capture(EVENTS.AMAZON_CTA_CLICKED, {
    source,
    asin,
    // Which page the click came from. Filled here, never by the caller, so it
    // cannot be forgotten — and the nav link needs it, being on every page.
    path: window.location.pathname,
    channel: attribution?.source ?? 'unknown',
    campaignId: attribution?.campaignId,
  });
}

export { posthog };
